import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Sparkles, Mail } from "lucide-react";

export default function Pricing({ embedded = false }) {
    const { t, lang } = useI18n();
    const { merchant } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState([]);
    const [period, setPeriod] = useState("yearly");
    const [loadingId, setLoadingId] = useState(null);

    useEffect(() => {
        api.get("/billing/plans").then(({ data }) => setPlans(data));
    }, []);

    const startCheckout = async (planId) => {
        if (!merchant) {
            // Save intent and bounce to login
            localStorage.setItem("wr_pending_plan", JSON.stringify({ planId, period }));
            nav("/login?next=/app/billing");
            return;
        }
        setLoadingId(planId);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan_id: planId,
                billing_period: period,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_checkout_failed"));
            setLoadingId(null);
        }
    };

    const fmt = (n) => new Intl.NumberFormat(lang === "en" ? "en-US" : "de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

    const yearlyMonthlyEq = (p) => (p.yearly / 12).toFixed(0);

    return (
        <div className={embedded ? "" : "min-h-screen bg-white"} data-testid="pricing-page">
            {!embedded && (
                <header className="border-b border-gray-200 bg-white/80 backdrop-blur sticky top-0 z-30">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                                <span className="text-white font-display font-bold">W</span>
                            </div>
                            <span className="font-display font-bold text-lg tracking-tight text-[#00153f]">
                                widerruf<span className="text-[#0b905d] text-sm font-semibold">.online</span>
                            </span>
                        </Link>
                        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f]" data-testid="back-to-landing">
                            <ArrowLeft className="w-4 h-4" /> {t("back")}
                        </Link>
                    </div>
                </header>
            )}

            <section className="bg-grain">
                <div className={`max-w-6xl mx-auto px-6 ${embedded ? "py-10" : "pt-16 pb-12"}`}>
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-[#0b905d] text-xs font-mono uppercase tracking-widest mb-5">
                            <Sparkles className="w-3.5 h-3.5" /> {t("pricing_eyebrow")}
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#00153f]">{t("pricing_title")}</h1>
                        <p className="mt-3 text-gray-600 max-w-2xl mx-auto">{t("pricing_sub")}</p>
                    </div>

                    {/* Toggle */}
                    <div className="mt-10 flex items-center justify-center">
                        <div className="inline-flex items-center bg-slate-100 rounded-full p-1 border border-gray-200" data-testid="billing-toggle">
                            {["monthly", "yearly"].map((p) => (
                                <button
                                    key={p}
                                    data-testid={`toggle-${p}`}
                                    onClick={() => setPeriod(p)}
                                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${period === p ? "bg-white text-[#00153f] shadow-sm" : "text-gray-500"}`}
                                >
                                    {t(p === "monthly" ? "billing_monthly" : "billing_yearly")}
                                    {p === "yearly" && (
                                        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-[#0b905d]">-17%</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Plans */}
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {plans.map((p, idx) => {
                            const popular = p.id === "business";
                            const onRequest = p.on_request === true;
                            const price = period === "monthly" ? p.monthly : yearlyMonthlyEq(p);
                            const features = lang === "en" ? p.features_en : p.features_de;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                                    className={`relative rounded-lg border bg-white p-7 flex flex-col ${popular ? "border-[#0b905d] shadow-lg" : "border-gray-200 shadow-sm"}`}
                                    data-testid={`plan-${p.id}`}
                                >
                                    {popular && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0b905d] text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">
                                            {t("pricing_popular")}
                                        </span>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="text-xs font-mono uppercase tracking-widest text-gray-500">{p.name}</div>
                                        {onRequest && (
                                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                                                {t("pricing_on_request")}
                                            </span>
                                        )}
                                    </div>
                                    {onRequest ? (
                                        <div className="mt-3">
                                            <span className="font-display text-2xl font-bold text-[#00153f]">{t("pricing_on_request_price")}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mt-3 flex items-baseline gap-1">
                                                <span className="font-display text-4xl font-bold text-[#00153f]">{fmt(price)}</span>
                                                <span className="text-sm text-gray-500">/ {t("pricing_per_month")}</span>
                                            </div>
                                            {period === "yearly" && (
                                                <div className="mt-1 text-xs text-gray-500">{t("pricing_billed_annually", { val: fmt(p.yearly) })}</div>
                                            )}
                                        </>
                                    )}
                                    <ul className="mt-6 space-y-3 flex-1">
                                        {features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                <Check className="w-4 h-4 text-[#0b905d] mt-0.5 flex-shrink-0" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {onRequest ? (
                                        <a
                                            href="mailto:hallo@widerruf.online?subject=Agency-Plan%20Anfrage"
                                            data-testid={`buy-${p.id}`}
                                            className="mt-7 h-11 inline-flex items-center justify-center rounded-md bg-[#00153f] hover:bg-[#001a4d] text-white text-sm font-medium px-4 transition-colors"
                                        >
                                            <Mail className="w-4 h-4 mr-2" /> {t("pricing_contact_us")}
                                        </a>
                                    ) : (
                                        <Button
                                            data-testid={`buy-${p.id}`}
                                            onClick={() => startCheckout(p.id)}
                                            disabled={loadingId === p.id}
                                            className={`mt-7 h-11 ${popular ? "bg-[#0b905d] hover:bg-[#067a4d]" : "bg-[#00153f] hover:bg-[#001a4d]"} text-white`}
                                        >
                                            {loadingId === p.id ? "..." : t("pricing_cta")} <ArrowRight className="ml-2 w-4 h-4" />
                                        </Button>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>

                    <p className="text-center text-xs text-gray-500 mt-10">{t("pricing_footnote")}</p>
                </div>
            </section>
        </div>
    );
}
